"use client";

/**
 * Where the event is, with the way to get directions there: the point on our
 * map, and Como chegar opening Google Maps with the directions. Nothing for an
 * event with no location, rather than a map of nowhere (hasLocation).
 */
import { Marker } from "react-map-gl/maplibre";
import { ExternalLink, LocationEdit } from "lucide-react";
import { track } from "@/lib/analytics";
import type { EventDetails } from "@/lib/types/event";
import { directionsUrl, safeHref } from "@/components/event/maps";
import { PIN } from "@/lib/maps";
import { placeLine } from "@/lib/jsonLd";
import { OurMap } from "@/components/ui/OurMap";

type Located = Pick<EventDetails, "id" | "location" | "google_maps_link" | "title" | "place">;

export const hasLocation = (event: Pick<EventDetails, "location"> | null | undefined) =>
  !!event && !!event.location && !!(Number(event.location.x) || Number(event.location.y));

/**
 * `showMap` false draws the frame without the map, for a page that is not sure
 * it is the one being looked at yet; the links are there regardless.
 */
export function EventLocation({ event, height = 220, showMap = true, className = "" }: { event: Located; height?: number; showMap?: boolean; className?: string }) {
  if (!hasLocation(event)) {
    return null;
  }
  return (
    <div className={className}>
      <EventLocationMap event={event} height={height} showMap={showMap} />
      <EventLocationLinks event={event} className="mt-2.5" />
    </div>
  );
}

/** the map alone, for a page that folds it away and keeps the links in sight (a phone's event page) */
export function EventLocationMap({ event, height = 220, showMap = true }: { event: Located; height?: number; showMap?: boolean }) {
  if (!hasLocation(event)) {
    return null;
  }
  const lat = Number(event.location!.y);
  const lng = Number(event.location!.x);

  return (
    <div className="overflow-hidden rounded-[14px] border border-line bg-ground" style={{ height }}>
      {showMap && (
        <OurMap center={{ lat, lng }} zoom={9} cooperative>
          <Marker longitude={lng} latitude={lat} anchor="bottom">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={PIN} width={30} height={40} alt="" title={event.title || ""} draggable={false} />
          </Marker>
        </OurMap>
      )}
    </div>
  );
}

/** Como chegar and, when the event has one, its own Google Maps link */
export function EventLocationLinks({ event, className = "" }: { event: Located; className?: string }) {
  if (!hasLocation(event)) {
    return null;
  }
  const directions = directionsUrl(event.location);
  const link = safeHref(event.google_maps_link);
  const where = placeLine(event.place);

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {/* the concelho and the park, in words: what the map shows, for whoever reads rather than looks */}
      {where && <p className="w-full text-[14px] text-ink-soft">{where}</p>}
      <a
        href={directions}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => track("navigate", { item_id: String(event.id), where: "localizacao" })}
        className="pressable inline-flex items-center gap-1.5 rounded-full bg-brand px-3.5 py-2 text-[13px] font-bold text-white hover:bg-brand-dark"
      >
        <LocationEdit size={14} /> Como chegar
      </a>
      {link && (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("navigate", { item_id: String(event.id), where: "localizacao_link" })}
          className="pressable inline-flex items-center gap-1.5 rounded-full border border-line-strong px-3.5 py-2 text-[13px] font-semibold text-ink"
        >
          <ExternalLink size={13} /> Ver no Google Maps
        </a>
      )}
    </div>
  );
}
